import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  type ElementRef,
  inject,
  linkedSignal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { CreateQuestionActionsComponent } from '../../components/admin-actions/create-question-actions';
import { TextSelectionPopoverComponent } from '../../components/annotations/text-selection-popover/text-selection-popover';
import { FooterComponent } from '../../components/footer/footer';
import { HeaderComponent } from '../../components/header/header';
import { MoreOptionsComponent } from '../../components/more-options/more-options';
import { NoteDialogManagerComponent } from '../../components/notes/note-dialog-manager/note-dialog-manager';
import { AnnotationDialogComponent } from '../../components/quiz/annotation-dialog/annotation-dialog';
import { QuizEditorDialogComponent } from '../../components/quiz/quiz-editor-dialog/quiz-editor-dialog';
import { QuizTakingDialogComponent } from '../../components/quiz/quiz-taking-dialog/quiz-taking-dialog';
import { VoiceRecordingDialogComponent } from '../../components/quiz/voice-recording-dialog/voice-recording-dialog';
import { UnansweredQuestionsDialogComponent } from '../../components/unanswered-questions-dialog/unanswered-questions-dialog';
import { QuestionsNavigationGuardService } from '../../services/questions-navigation-guard/questions-navigation-guard';
import { ResourceSkeletonComponent } from '../../components/resource-skeleton/resource-skeleton';
import { SearchBarComponent } from '../../components/search/search-bar/search-bar';
import { TextInfoComponent } from '../../components/text-info/text-info';
import { TtsComponent } from '../../components/tts/tts';
import { PaymentBlockedDialogComponent } from '../../components/payment-blocked-dialog/payment-blocked-dialog';
import { PaymentStatusService } from '../../services/payment-status/payment-status';
import { UnauthorizedDialogComponent } from '../../components/unauthorized-dialog/unauthorized-dialog';
import { OfflineAlertComponent } from '../../components/offline-alert/offline-alert';
import { OfflineReadingDialogComponent } from '../../components/offline-reading-dialog/offline-reading-dialog';
import { OfflineDownloadService } from '../../services/offline/offline-download';
import { ControlsVisibility } from '../../services/controls-visibility/controls-visibility';
import { FeatureFlagsService } from '../../services/feature-flags/feature-flags';
import { MetadataService } from '../../services/metadata/metadata';
import { QuizService } from '../../services/quiz/quiz';
import { ReaderNavigatorService } from '../../services/readium/navigator/reader-navigator';
import { ReadingStatsService } from '../../services/reading-stats/reading-stats';
import { SettingsService } from '../../services/readium/settings/settings';
import { HighlightsService } from '../../services/selections/highlights';
import { TtsService } from '../../services/tts/tts';
import { NavigationButtonComponent } from './components/navigation-button/navigation-button';

@Component({
  selector: 'nr-reader',
  imports: [
    HeaderComponent,
    FooterComponent,
    ResourceSkeletonComponent,
    CreateQuestionActionsComponent,
    TextSelectionPopoverComponent,
    TextInfoComponent,
    NavigationButtonComponent,
    SearchBarComponent,
    NoteDialogManagerComponent,
    MoreOptionsComponent,
    TranslatePipe,
    UnauthorizedDialogComponent,
    PaymentBlockedDialogComponent,
    TtsComponent,
    QuizEditorDialogComponent,
    QuizTakingDialogComponent,
    AnnotationDialogComponent,
    VoiceRecordingDialogComponent,
    UnansweredQuestionsDialogComponent,
    OfflineAlertComponent,
    OfflineReadingDialogComponent,
  ],
  templateUrl: './reader.html',
  styleUrl: './reader.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ReaderComponent {
  private readonly navigatorService = inject(ReaderNavigatorService);
  private readonly highlightsService = inject(HighlightsService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly metadataService = inject(MetadataService);
  private readonly ttsService = inject(TtsService);
  private readonly controlsVisibility = inject(ControlsVisibility);
  private readonly quizService = inject(QuizService);

  private readonly viewport = viewChild.required<ElementRef<HTMLDivElement>>('container');
  private readonly routeData = toSignal(this.activatedRoute.data);

  readonly featureFlags = inject(FeatureFlagsService);
  // AC2 asks for the affirmative case to be invisible; without this the reactive rollback shows
  // as a brief jump forward and back while the verdict is in flight.
  readonly isCheckingPayment = inject(PaymentStatusService).isChecking;
  readonly readerBackgroundColor = computed(
    () => this.navigatorService.navigatorPreferences()?.backgroundColor ?? 'transparent',
  );
  readonly isResourceLoaded = computed(
    () =>
      this.navigatorService.resourceLoaded.hasValue() &&
      this.navigatorService.resourceLoaded.value(),
  );
  readonly isUnauthorized = this.metadataService.isUnauthorized;
  readonly selectionRect = linkedSignal<DOMRect | undefined>(() =>
    this.navigatorService.selectionRect(),
  );
  readonly isTTSDialogOpen = this.ttsService.isDialogOpen.asReadonly();
  readonly isHeaderFooterVisible = this.controlsVisibility.isHeaderFooterVisible;
  readonly showTeacherQuestions = computed<boolean>(
    () => this.metadataService.canManageQuestions() && this.featureFlags.isEnabled('questions')(),
  );
  readonly quizDraft = this.quizService.quizDraft.asReadonly();
  readonly quizToAnswer = this.quizService.quizToAnswer.asReadonly();
  readonly quizMode = this.quizService.quizMode.asReadonly();
  readonly isOfflineDialogOpen = inject(OfflineDownloadService).isDialogOpen.asReadonly();

  constructor() {
    inject(SettingsService);
    inject(QuestionsNavigationGuardService);
    inject(ReadingStatsService);

    const data = this.routeData();
    if (data) {
      const token = typeof data['token'] === 'string' ? data['token'] : '';
      this.metadataService.loadMetadata(token);
    }

    afterNextRender(() => {
      if (!this.isUnauthorized()) {
        this.navigatorService.load(this.viewport().nativeElement);
      }
    });

    effect(() => {
      this.highlightsService.loadVisibleHighlights();
    });
  }

  prev(): void {
    this.navigatorService.prev();
  }

  next(): void {
    this.navigatorService.next();
  }
}
